import { MathManager } from '@payments-checkout/shared/helpers/index';
import type { OrderStatusStep } from '../orderStatusTracker/orderStatusTracker';
import type { SessionStatusResponse } from '../../types/sessionStatus.types';
import type { CreditCardPaymentInfo } from '../paymentInfoDisplay/types';
import { formatPaymentDate } from '../trackingStatus/statusDate';

type FormatMessage = (descriptor: { id: string }) => string;
type FormatDate = (date: Date, options: Intl.DateTimeFormatOptions) => string;

export type CreditCardState = 'approved' | 'processing' | 'refused';

const CARD_BRANDS: CreditCardPaymentInfo['brand'][] = [
  'visa',
  'mastercard',
  'elo',
  'amex',
  'dinersclub',
  'discover',
  'jcb',
];

export function resolveCreditCardState(orderStatus?: string): CreditCardState {
  if (orderStatus === 'paid') return 'approved';
  if (orderStatus === 'failed') return 'refused';
  return 'processing';
}

export function normalizeCardBrand(
  brand?: string
): CreditCardPaymentInfo['brand'] {
  const normalized = (brand ?? '').toLowerCase() as CreditCardPaymentInfo['brand'];
  return CARD_BRANDS.includes(normalized) ? normalized : 'visa';
}

export function buildCreditCardPaymentInfo(
  sessionData: SessionStatusResponse,
  state: CreditCardState
): CreditCardPaymentInfo | undefined {
  const payment = sessionData?.payments?.[0];

  if (state !== 'approved' || !payment?.card) return undefined;

  const totalInCents = sessionData?.summary?.total ?? 0;
  const installments = payment.installments ?? 1;

  return {
    paymentMethod: 'credit_card',
    brand: normalizeCardBrand(payment.card.brand),
    lastDigits: payment.card.last_four_digits,
    installments,
    installmentAmount: totalInCents / 100 / (installments || 1),
    customerName: payment.card.holder_name || sessionData?.customer?.name || '',
    customerDocument: sessionData?.customer?.document || '',
  };
}

function getCreditCardTrackerItems(
  formatMessage: FormatMessage
): OrderStatusStep[] {
  return [
    {
      label: formatMessage({ id: 'statusPage.creditCard.step.order' }),
      icon: 'bag',
      status: 'completed',
    },
    {
      label: formatMessage({ id: 'statusPage.creditCard.step.analysis' }),
      icon: 'circle-clock',
      status: 'current',
    },
    {
      label: formatMessage({ id: 'statusPage.creditCard.step.approved' }),
      icon: 'check',
      status: 'next',
    },
  ];
}

interface CreditCardFeedbackParams {
  sessionData: SessionStatusResponse;
  state: CreditCardState;
  formatMessage: FormatMessage;
  formatDate: FormatDate;
  onPrintReceipt: () => void;
}

export interface CreditCardFeedback {
  title: string;
  subtitle?: string;
  amount?: string;
  icon?: string;
  variant?: string;
  offeredBy?: string;
  orderId?: string;
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  statusTracker?: OrderStatusStep[];
  alert?: {
    description: string;
  };
}

export function buildCreditCardFeedback({
  sessionData,
  state,
  formatMessage,
  formatDate,
  onPrintReceipt,
}: CreditCardFeedbackParams): CreditCardFeedback {
  const totalInCents = sessionData?.summary?.total ?? 0;
  const amount = MathManager.formatCurrency({ value: totalInCents / 100 });

  const dateSource =
    state === 'approved' ? sessionData?.paid_at : sessionData?.created_at;
  const subtitle = formatPaymentDate(dateSource, formatDate, formatMessage);

  const offeredBy = `${formatMessage({
    id: 'statusPage.creditCard.soldBy',
  })} ${sessionData?.merchant?.name}`;
  const orderId = `${formatMessage({
    id: 'statusPage.creditCard.orderLabel',
  })} ${sessionData?.order_id?.toUpperCase()}`;

  const base = { amount, subtitle, offeredBy, orderId };

  if (state === 'approved') {
    return {
      ...base,
      icon: 'check',
      variant: 'positive',
      title: formatMessage({ id: 'statusPage.purchaseApproved' }),
      actionLabel: formatMessage({
        id: 'statusPage.creditCard.downloadReceipt',
      }),
      actionDisabled: false,
      onAction: onPrintReceipt,
    };
  }

  if (state === 'refused') {
    return {
      ...base,
      icon: 'circle-error',
      variant: 'negative',
      title: formatMessage({ id: 'statusPage.creditCard.refused' }),
      alert: {
        description: formatMessage({ id: 'statusPage.creditCard.refusedAlert' }),
      },
    };
  }

  return {
    ...base,
    title: formatMessage({ id: 'statusPage.creditCard.trackOrder' }),
    statusTracker: getCreditCardTrackerItems(formatMessage),
  };
}
